import type { UIMessage } from '@ai-sdk/react'
import type { FileUIPart } from 'ai'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type BuddyEvent,
  type BuddyFeedbackReason,
  isFeedbackReason,
  parseBuddyStream,
} from './buddy-stream'

export type BuddyStatus = 'ready' | 'submitted' | 'streaming' | 'error'

export interface BuddyAttachment {
  url: string
  mediaType: string
  filename?: string
}

export interface BuddyUserContext {
  email?: string
  name?: string
  roles?: string[]
  plan?: string
  balance?: number
  currency?: string
  conversionRate?: number
  region?: string
  countryId?: string
  registeredIp?: string
  signupDate?: string
}

export interface BuddyChatBody {
  userContext: BuddyUserContext
  pageUrl: string
}

interface UseBuddyChatOptions {
  endpoint: string
  buildHeaders: () => Record<string, string>
  buildBody: () => BuddyChatBody
  // Identifies the org that owns the conversation id, so it is never carried across
  // a logout or org switch. Resolved lazily because the profile query it reads from
  // has usually not settled on first render.
  getSessionOwner?: () => string
  // Opts this client into aiassist's feedback_prompt. Off unless the caller renders the
  // prompt and its kill switch is off; opted out, aiassist skips the classifier call too.
  feedbackPromptsEnabled?: boolean
  onFinish?: (summary: BuddyResponseSummary) => void
  onError?: (error: Error) => void
}

// Buddy server caps history at 20 turns; trim client-side so the request body
// is smaller and we never get rejected at the boundary.
const MAX_HISTORY_TURNS = 20

type Part = UIMessage['parts'][number]
type ToolPart = Extract<Part, { type: `tool-${string}` }>

const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2)}-${Date.now()}`

// Conversation id for Buddy chat analytics/threading (PostHog, escalation grouping).
// Client-minted — aiassist has no server-issued session event — and kept in
// sessionStorage so a reload within the tab continues the same conversation; a new
// tab or a cleared chat gets a fresh one. Storage access is best-effort: private
// browsing / a disabled store must not break the chat, just the reload-continuity.
//
// Stored WITH its owning org, because sessionStorage outlives both logout and an
// org switch: logoutSuccess() clears localStorage only (v2/shared/hooks/use-auth.tsx)
// and both flows then hard-reload, so an unscoped id would be inherited by the next
// tenant in that tab and would group two orgs' turns — and their escalation
// threads — into one conversation. A different owner always mints a fresh id.
const BUDDY_SESSION_STORAGE_KEY = 'buddy_session_id'

interface StoredSession {
  owner: string
  id: string
}

const readStoredSession = (): StoredSession | null => {
  try {
    const raw = sessionStorage.getItem(BUDDY_SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredSession> | null
    // Also drops the pre-scoping bare-string format, which has no owner and so
    // cannot be proven to belong to the current org.
    return parsed && typeof parsed.id === 'string' && typeof parsed.owner === 'string'
      ? { owner: parsed.owner, id: parsed.id }
      : null
  } catch {
    return null
  }
}

const writeStoredSession = (session: StoredSession): void => {
  try {
    sessionStorage.setItem(BUDDY_SESSION_STORAGE_KEY, JSON.stringify(session))
  } catch {
    // sessionStorage full/disabled — id still works for this turn, just won't
    // survive a reload
  }
}

// Reuses the stored id only when it belongs to the same org, else mints a fresh one.
// An empty owner means the profile query has not resolved yet; that cannot prove a
// mismatch, so the stored id is reused and rebinds once the real owner arrives.
export const getOrCreateSessionId = (owner: string): string => {
  const stored = readStoredSession()
  if (stored && (!owner || stored.owner === owner)) return stored.id
  const fresh = newId()
  writeStoredSession({ owner, id: fresh })
  return fresh
}

// Starts a new conversation: overwrites the stored id and returns it.
export const resetSessionId = (owner: string): string => {
  const fresh = newId()
  writeStoredSession({ owner, id: fresh })
  return fresh
}

const messageText = (m: UIMessage): string =>
  m.parts
    .filter((p): p is Extract<Part, { type: 'text' }> => p.type === 'text')
    .map((p) => p.text)
    .join('')

const messageAttachments = (m: UIMessage): BuddyAttachment[] =>
  m.parts
    .filter((p): p is Extract<Part, { type: 'file' }> => p.type === 'file')
    .map((p) => ({ url: p.url, mediaType: p.mediaType, filename: p.filename }))

export interface BuddyFeedbackPrompt {
  reason: BuddyFeedbackReason
  apiId: string | null
}

// The agent side of a completed turn: what buddy_message_sent records, plus any prompt.
export interface BuddyResponseSummary {
  responseText: string
  toolsUsed: string[]
  hadError: boolean
  sessionId?: string
  // Set only when the server asked for a feedback prompt on this turn.
  feedbackPrompt?: BuddyFeedbackPrompt
}

// Distils a finished assistant message into analytics fields: the rendered
// answer, the tools Buddy called, and whether any tool errored. onFinish passes
// this through so PostHog captures Buddy's reply, not just the user's message.
export const summarizeAssistant = (
  msg: UIMessage | undefined,
): Omit<BuddyResponseSummary, 'sessionId' | 'feedbackPrompt'> => {
  if (!msg) return { responseText: '', toolsUsed: [], hadError: false }
  const toolsUsed: string[] = []
  let hadError = false
  for (const p of msg.parts) {
    if (p.type.startsWith('tool-')) {
      toolsUsed.push(p.type.slice('tool-'.length))
      if ((p as ToolPart).state === 'output-error') hadError = true
    }
  }
  return { responseText: messageText(msg), toolsUsed, hadError }
}

const updateLastPart = <T extends Part>(
  parts: Part[],
  predicate: (p: Part) => p is T,
  updater: (p: T) => T,
): Part[] => {
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i]
    if (predicate(p)) {
      const next = [...parts]
      next[i] = updater(p)
      return next
    }
  }
  return parts
}

// Find the last in-progress tool part with a matching name. Used by tool_output
// events that arrive without a toolCallId — the runner does emit one per call,
// but using name as fallback handles a class of races without sacrificing the
// happy path.
const findInProgressTool = (parts: Part[], name: string): number => {
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i]
    if (p.type === `tool-${name}` && (p as ToolPart).state === 'input-available') {
      return i
    }
  }
  return -1
}

const applyEvent = (msg: UIMessage, event: BuddyEvent): UIMessage => {
  switch (event.type) {
    case 'start':
      return msg
    case 'token': {
      const delta = event.data.text
      if (!delta) return msg
      // First close any in-flight reasoning (narration) so the UI flips from
      // shimmer → "Thought" toggle the moment real tokens start streaming.
      const closedReasoning = updateLastPart(
        msg.parts,
        (p): p is Extract<Part, { type: 'reasoning' }> =>
          p.type === 'reasoning' && (p as { state?: string }).state === 'streaming',
        (p) => ({ ...p, state: 'done' as const }),
      )
      // Append delta to last text part, or create one.
      const last = closedReasoning[closedReasoning.length - 1]
      if (last && last.type === 'text') {
        const next = [...closedReasoning]
        next[next.length - 1] = { ...last, text: last.text + delta, state: 'streaming' }
        return { ...msg, parts: next }
      }
      return {
        ...msg,
        parts: [...closedReasoning, { type: 'text', text: delta, state: 'streaming' }],
      }
    }
    case 'message': {
      const text = event.data.text
      if (!text) return msg
      const hasText = msg.parts.some((p) => p.type === 'text')
      if (hasText) return msg // tokens already streamed full answer
      return {
        ...msg,
        parts: [...msg.parts, { type: 'text', text, state: 'done' }],
      }
    }
    case 'narration': {
      const text = event.data.text
      if (!text) return msg
      // Coalesce consecutive narrations into a single reasoning part.
      const last = msg.parts[msg.parts.length - 1]
      if (last && last.type === 'reasoning' && (last as { state?: string }).state === 'streaming') {
        const next = [...msg.parts]
        next[next.length - 1] = { ...last, text: `${last.text}\n${text}` }
        return { ...msg, parts: next }
      }
      return {
        ...msg,
        parts: [...msg.parts, { type: 'reasoning', text, state: 'streaming' }],
      }
    }
    case 'tool_call': {
      const { name, args } = event.data
      const toolCallId = newId()
      return {
        ...msg,
        parts: [
          ...msg.parts,
          {
            type: `tool-${name}` as const,
            toolCallId,
            state: 'input-available',
            input: args,
          } as ToolPart,
        ],
      }
    }
    case 'tool_output': {
      const { name, ...rest } = event.data
      const idx = findInProgressTool(msg.parts, name)
      const isError = rest.success === false
      if (idx === -1) {
        // Output arrived without a prior tool_call (shouldn't happen, but
        // emit a finished tool part so the UI can show it).
        return {
          ...msg,
          parts: [
            ...msg.parts,
            {
              type: `tool-${name}` as const,
              toolCallId: newId(),
              state: isError ? 'output-error' : 'output-available',
              output: rest,
            } as ToolPart,
          ],
        }
      }
      const next = [...msg.parts]
      const existing = next[idx] as ToolPart
      next[idx] = {
        ...existing,
        state: isError ? 'output-error' : 'output-available',
        output: rest,
      } as ToolPart
      return { ...msg, parts: next }
    }
    case 'sources': {
      const sources = event.data.sources || []
      if (!sources.length) return msg
      return {
        ...msg,
        parts: [
          ...msg.parts,
          ...sources.map((s) => ({
            type: 'source-url' as const,
            sourceId: newId(),
            url: s.url,
            title: s.title,
          })),
        ],
      }
    }
    case 'final': {
      // Mark any streaming text/reasoning parts as done.
      const parts = msg.parts.map((p) => {
        if (
          (p.type === 'text' || p.type === 'reasoning') &&
          (p as { state?: string }).state === 'streaming'
        ) {
          return { ...p, state: 'done' as const }
        }
        return p
      })
      return { ...msg, parts }
    }
    case 'result': {
      // PAI contract: the complete, sanitized answer (no token deltas). Flip any
      // streaming parts to done and set the answer text — replacing a streamed
      // text part if one exists (transition safety), else appending one.
      const text = event.data.text
      if (!text) return msg
      let setText = false
      const parts = msg.parts.map((p) => {
        const streaming = (p as { state?: string }).state === 'streaming'
        if (p.type === 'text' && streaming) {
          setText = true
          return { ...p, text, state: 'done' as const }
        }
        if (p.type === 'reasoning' && streaming) {
          return { ...p, state: 'done' as const }
        }
        return p
      })
      return {
        ...msg,
        parts: setText ? parts : [...parts, { type: 'text', text, state: 'done' }],
      }
    }
    case 'session':
      // Not currently emitted by aiassist (session id is client-minted — see
      // getOrCreateSessionId above); harmless no-op if a future stream sends one.
      return msg
    case 'cost':
      // Internal $ metric — never rendered to the customer.
      return msg
    case 'done': {
      // PAI-contract stream terminator. Same as `final`: flip streaming → done.
      const parts = msg.parts.map((p) =>
        (p.type === 'text' || p.type === 'reasoning') &&
        (p as { state?: string }).state === 'streaming'
          ? { ...p, state: 'done' as const }
          : p,
      )
      return { ...msg, parts }
    }
    case 'error':
      // Caller throws based on this in the for-await loop.
      return msg
    default:
      return msg
  }
}

export const useBuddyChat = (opts: UseBuddyChatOptions) => {
  const [messages, setMessages] = useState<UIMessage[]>([])
  const [status, setStatus] = useState<BuddyStatus>('ready')
  const [error, setError] = useState<Error | null>(null)
  const messagesRef = useRef<UIMessage[]>([])
  const abortRef = useRef<AbortController | null>(null)
  // Client-minted conversation id (see getOrCreateSessionId above); replayed on
  // every turn and refreshed whenever the conversation resets. Bound at send time
  // rather than on first render, so it is resolved against a settled profile and
  // re-mints if the owning org changed.
  const sessionIdRef = useRef<string>('')
  // useEffect cleanup needs the latest abort handle without re-binding.
  const optsRef = useRef(opts)
  optsRef.current = opts

  const writeMessages = useCallback((updater: (prev: UIMessage[]) => UIMessage[]) => {
    const next = updater(messagesRef.current)
    messagesRef.current = next
    // Deliberately does NOT touch the session id. An empty list is not proof of a
    // reset: a 429 on the very first turn drops that message (assistant-panel's
    // error effect), and the retry must stay in the same conversation. Only
    // resetConversation() starts a new one.
    setMessages(next)
  }, [])

  // The one explicit "new conversation" action: clears the thread and mints a fresh
  // conversation id together, so the two can never drift apart.
  const resetConversation = useCallback(() => {
    sessionIdRef.current = resetSessionId(optsRef.current.getSessionOwner?.() ?? '')
    writeMessages(() => [])
  }, [writeMessages])

  const setMessagesPublic = useCallback(
    (updater: UIMessage[] | ((prev: UIMessage[]) => UIMessage[])) => {
      writeMessages((prev) =>
        typeof updater === 'function'
          ? (updater as (p: UIMessage[]) => UIMessage[])(prev)
          : updater,
      )
    },
    [writeMessages],
  )

  const clearError = useCallback(() => setError(null), [])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    // Reset to idle so the panel doesn't stay stuck in submitted/streaming after
    // a deliberate cancel — otherwise the send button stays disabled.
    setStatus('ready')
  }, [])

  const sendMessage = useCallback(
    async ({ text, files }: { text: string; files?: FileUIPart[] }) => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      setError(null)
      setStatus('submitted')

      const trimmedText = text || ''
      const userParts: Part[] = []
      if (trimmedText) userParts.push({ type: 'text', text: trimmedText })
      for (const f of files || []) {
        userParts.push({
          type: 'file',
          mediaType: f.mediaType,
          filename: f.filename,
          url: f.url,
        })
      }
      const userMsg: UIMessage = {
        id: newId(),
        role: 'user',
        parts: userParts,
      }

      // Snapshot history BEFORE appending the new user msg (matches aiassist
      // BuddyChatRequest contract: `message` is the current turn, `history` is
      // everything prior).
      const prior = messagesRef.current
      const history = prior
        .map((m) => ({
          role: m.role === 'user' ? ('user' as const) : ('assistant' as const),
          text: messageText(m),
          attachments: messageAttachments(m),
        }))
        .filter((t) => t.text || t.attachments?.length)
        .slice(-MAX_HISTORY_TURNS)

      writeMessages((prev) => [...prev, userMsg])

      const attachments: BuddyAttachment[] = (files || []).map((f) => ({
        url: f.url,
        mediaType: f.mediaType,
        filename: f.filename,
      }))

      const { userContext, pageUrl } = optsRef.current.buildBody()
      // Resolve the id here, alongside buildBody, so it is keyed on the org this
      // turn actually belongs to rather than whatever was in the tab beforehand.
      sessionIdRef.current = getOrCreateSessionId(optsRef.current.getSessionOwner?.() ?? '')
      const body = {
        message: trimmedText,
        history,
        attachments,
        userContext,
        pageUrl,
        // Send the conversation id every turn so aiassist can thread this exchange
        // with the rest of the conversation (PostHog + escalation grouping).
        ...(sessionIdRef.current ? { session_id: sessionIdRef.current } : {}),
        feedback_prompts_enabled: optsRef.current.feedbackPromptsEnabled ?? false,
      }

      let response: Response
      try {
        response = await fetch(optsRef.current.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...optsRef.current.buildHeaders(),
          },
          body: JSON.stringify(body),
          signal: ctrl.signal,
        })
      } catch (e) {
        if (ctrl.signal.aborted) {
          setStatus('ready')
          return
        }
        const err = e instanceof Error ? e : new Error('Network error')
        setError(err)
        setStatus('error')
        optsRef.current.onError?.(err)
        return
      }

      if (!response.ok) {
        const err = new Error(
          response.status === 429 ? '429 Rate limit exceeded' : `HTTP ${response.status}`,
        )
        setError(err)
        setStatus('error')
        optsRef.current.onError?.(err)
        return
      }

      setStatus('streaming')
      const assistantId = newId()
      writeMessages((prev) => [...prev, { id: assistantId, role: 'assistant', parts: [] }])

      let feedbackPrompt: BuddyFeedbackPrompt | undefined
      try {
        for await (const event of parseBuddyStream(response, ctrl.signal)) {
          if (event.type === 'error') {
            throw new Error(event.data.error || 'Buddy stream failed')
          }
          if (event.type === 'feedback_prompt') {
            if (isFeedbackReason(event.data.reason)) {
              feedbackPrompt = { reason: event.data.reason, apiId: event.data.api_id ?? null }
            }
            continue
          }
          writeMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? applyEvent(m, event) : m)),
          )
        }
        // Close out any still-streaming parts on normal stream end (server
        // didn't emit `final` — defensive cleanup).
        writeMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantId) return m
            return applyEvent(m, { type: 'final', data: {} })
          }),
        )
        setStatus('ready')
        const finalAssistant = messagesRef.current.find((m) => m.id === assistantId)
        // A mid-stream reset clears messagesRef; skip analytics rather than record
        // an empty response paired with stale pending metadata.
        if (!finalAssistant) return
        optsRef.current.onFinish?.({
          ...summarizeAssistant(finalAssistant),
          sessionId: sessionIdRef.current || undefined,
          // An abort can end the stream without throwing; that turn is no longer one to rate.
          feedbackPrompt: ctrl.signal.aborted ? undefined : feedbackPrompt,
        })
      } catch (e) {
        if (ctrl.signal.aborted) {
          setStatus('ready')
          return
        }
        const err = e instanceof Error ? e : new Error('Stream error')
        setError(err)
        setStatus('error')
        optsRef.current.onError?.(err)
      } finally {
        if (abortRef.current === ctrl) abortRef.current = null
      }
    },
    [writeMessages],
  )

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    [],
  )

  return {
    messages,
    sendMessage,
    setMessages: setMessagesPublic,
    resetConversation,
    status,
    error,
    clearError,
    stop,
  }
}
